import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { CustomerLink, OrderLink } from "@/components/EntityLink";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { formatUkMsisdn, normalizeIccid } from "@ewn/shared";
import { readEsim, refreshEsim, type AdminEsimRecord } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { bytes, money, when } from "@/lib/format";
import { statusBadge } from "@/screens/OrdersScreen";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DetailList, DetailRow } from "@/components/ui/detail-row";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

interface Asked {
  profile: boolean;
  usage: boolean;
}

/** What the supplier did not answer about, or null when it answered both. */
function unanswered(asked: Asked): string | null {
  if (asked.profile && asked.usage) return null;
  if (asked.profile) return "the usage";
  if (asked.usage) return "the profile";
  return "the profile or the usage";
}

/**
 * The route's element: one screen per line.
 *
 * KEYED ON THE ICCID, so moving to another line mounts a fresh screen. What
 * one line's page loaded, was told or was refused -- including an answer that
 * arrives after the operator has moved on -- cannot land under another line's
 * heading, because the page it was meant for is gone.
 */
export function EsimScreen() {
  const { iccid = "" } = useParams();
  return <EsimLine key={iccid} iccid={iccid} />;
}

/**
 * One line: what the supplier last told us about it, and a way to ask again.
 *
 * EVERY FIGURE HERE IS THE SUPPLIER'S, from the last time it answered. The
 * time shown is when it was last ASKED, which is stamped whatever the answer:
 * a notice after Refresh says whether it answered. The sweeps ask every few
 * hours; Refresh asks now. It is refused shortly after the last ask,
 * because the supplier account is shared with a sibling product, and the
 * refusal says so rather than looking like a fault.
 */
function EsimLine({ iccid }: { iccid: string }) {
  const { data, loading, error, reload } = useAsync(() => readEsim(iccid), [iccid]);
  const action = useAction();
  const [asked, setAsked] = React.useState<Asked | null>(null);
  const missing = asked ? unanswered(asked) : null;

  async function refresh() {
    setAsked(null);
    await action.run(async () => {
      const answer = await refreshEsim(iccid);
      setAsked({ profile: answer.profile, usage: answer.usage });
      return answer;
    });
    reload();
  }

  return (
    <div className="space-y-4">
      <PageHeader title={normalizeIccid(iccid)} pretitle="eSIM">
        <Button variant="outline" size="sm" onClick={() => history.back()}>
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          Back
        </Button>
        {data && !data.esim.simulated && (
          <Button size="sm" onClick={() => void refresh()} disabled={action.busy}>
            <RefreshCw aria-hidden className="h-3.5 w-3.5" />
            {action.busy ? "Asking…" : "Refresh from supplier"}
          </Button>
        )}
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}
      {asked && (
        // Each half says whether the supplier ANSWERED, not whether anything
        // changed: an answer that matches what we had is still an answer.
        <Notice tone={missing ? "warning" : "success"}>
          {missing
            ? `The supplier did not answer about ${missing}. ` +
              "The figures below are the last ones it gave."
            : "The supplier answered. The figures below are current."}
        </Notice>
      )}

      {loading && !data ? (
        <Card>
          <CardContent className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ) : !data ? (
        // Nothing to show. Why is in the error above: the worker answers an
        // unknown ICCID with a 404, whose message says so.
        <Link to="/esims" className="text-sm underline underline-offset-4">
          Back to the list
        </Link>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <LineFacts esim={data.esim} />
            <UsageFacts esim={data.esim} />
            {data.topups.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top-ups</CardTitle>
                </CardHeader>
                {data.topups.map((topup) => (
                  <DetailList key={topup.id}>
                    <DetailRow label="Order">
                      {topup.order_code ? <OrderLink code={topup.order_code} /> : "—"}
                    </DetailRow>
                    <DetailRow label="Package">{topup.package_id}</DetailRow>
                    <DetailRow label="Price">{money(topup.price_cents)}</DetailRow>
                    <DetailRow label="Status">{topup.status}</DetailRow>
                    <DetailRow label="Created">{when(topup.created_at)}</DetailRow>
                  </DetailList>
                ))}
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Order</CardTitle>
              </CardHeader>
              {data.order ? (
                <DetailList>
                  <DetailRow label="Code">
                    <OrderLink code={data.order.order_code} />
                  </DetailRow>
                  <DetailRow label="Status">{statusBadge(data.order.status)}</DetailRow>
                  <DetailRow label="Package">{data.order.package_id}</DetailRow>
                  <DetailRow label="Customer">
                    {data.order.email ? <CustomerLink email={data.order.email} /> : "—"}
                  </DetailRow>
                </DetailList>
              ) : (
                <CardContent className="text-sm text-muted-foreground">
                  The order is missing.
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function LineFacts({ esim }: { esim: AdminEsimRecord }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Line</CardTitle>
        {esim.simulated ? <Badge color="gray">never issued by the supplier</Badge> : null}
      </CardHeader>
      <DetailList>
        <DetailRow label="Number">
          {esim.msisdn ? (
            formatUkMsisdn(esim.msisdn)
          ) : (
            <Badge color="amber">not yet assigned</Badge>
          )}
        </DetailRow>
        <DetailRow label="Profile">{esim.profile_state ?? "—"}</DetailRow>
        <DetailRow label="First installed">{when(esim.first_installed_at)}</DetailRow>
        {/* Two clocks, and they are not the same: the bundle's allowance runs
            out on the first, the number itself is recycled on the second. */}
        <DetailRow label="Bundle expires">{when(esim.bundle_expires_at)}</DetailRow>
        <DetailRow label="Number kept until">{when(esim.line_keepalive_deadline)}</DetailRow>
        <DetailRow label="Number last checked">{when(esim.msisdn_checked_at)}</DetailRow>
        <DetailRow label="Delivered">{when(esim.created_at)}</DetailRow>
        <DetailRow label="LPA" mono>
          {esim.lpa}
        </DetailRow>
        {esim.apple_install_url && (
          <DetailRow label="iPhone install link" mono>
            {esim.apple_install_url}
          </DetailRow>
        )}
        {esim.android_install_url && (
          <DetailRow label="Android install link" mono>
            {esim.android_install_url}
          </DetailRow>
        )}
      </DetailList>
    </Card>
  );
}

function UsageFacts({ esim }: { esim: AdminEsimRecord }) {
  const total = esim.data_total_bytes;
  const used = esim.data_used_bytes;
  const left = total != null && used != null ? Math.max(0, total - used) : null;
  const share = total && left != null ? Math.round((left / total) * 100) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usage</CardTitle>
        <span className="text-xs text-muted-foreground">
          last asked {when(esim.usage_updated_at)}
        </span>
      </CardHeader>
      {share != null && (
        <CardContent className="pb-0">
          <div
            className="h-2 overflow-hidden rounded bg-muted"
            role="meter"
            aria-label="Data left"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={share}
          >
            <div className="h-full bg-primary-strong" style={{ width: `${share}%` }} />
          </div>
        </CardContent>
      )}
      <DetailList>
        <DetailRow label="Plan">
          <span className="font-mono text-xs">{esim.bundle_name ?? "—"}</span>
        </DetailRow>
        <DetailRow label="Bundle state">{esim.last_bundle_state ?? "—"}</DetailRow>
        <DetailRow label="Data left">
          {left == null
            ? "—"
            : `${bytes(left)} of ${bytes(total)}${share == null ? "" : ` (${share}%)`}`}
        </DetailRow>
        <DetailRow label="Texts left">{esim.sms_remaining ?? "—"}</DetailRow>
        <DetailRow label="Minutes left">{esim.voice_remaining ?? "—"}</DetailRow>
      </DetailList>
    </Card>
  );
}
